import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  description?: string;
  /** Alias for `description` */
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  /** Alias for `variant` */
  confirmVariant?: 'danger' | 'primary' | string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  /** Alias for `onCancel` */
  onClose?: () => void;
}

export function ConfirmModal({
  open,
  title,
  description,
  message,
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  variant      = 'danger',
  confirmVariant,
  loading      = false,
  onConfirm,
  onCancel,
  onClose,
}: ConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const handleCancel = onCancel ?? onClose ?? (() => {});
  const body = description ?? message;
  const effectiveVariant = (confirmVariant ?? variant) as 'danger' | 'primary' | string;

  // Focus trap — focus cancel button on open
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, handleCancel]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="backdrop"
        onClick={handleCancel}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby={body ? 'confirm-modal-desc' : undefined}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-[var(--radius-lg)] w-full max-w-md card-shadow-lg p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h2 id="confirm-modal-title" className="text-base font-semibold text-[var(--color-text-primary)]">
              {title}
            </h2>
            <button
              onClick={handleCancel}
              aria-label="Close dialog"
              className="p-1 rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>

          {body && (
            <p id="confirm-modal-desc" className="text-sm text-[var(--color-text-secondary)] mb-6">
              {body}
            </p>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              ref={cancelRef}
              variant="secondary"
              size="sm"
              onClick={handleCancel}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={effectiveVariant === 'danger' ? 'danger' : 'primary'}
              size="sm"
              loading={loading}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
